import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'

export interface FqDialogNavSection {
  /** Chiave univoca della sezione (usata per il binding di `active`). */
  key: string
  /** Etichetta mostrata nella navigazione. */
  label: string
  /** Icona FontAwesome opzionale (es. ['fas', 'list']). */
  icon?: any
  /** Contatore opzionale mostrato dopo l'etichetta (es. versioni nello storico). */
  badge?: number | string
}

export type FqDialogNavVariant = 'auto' | 'tabs' | 'rail' | 'wizard' | 'card' | 'pill'

/**
 * Navigazione a sezioni per i contenuti dei dialog.
 * Varianti: `pill` (default consigliato), `tabs`, `card`, `rail`, `wizard`.
 * Con `[enclosed]="true"` (pill/tabs) la barra diventa l'intestazione di un
 * contenitore arrotondato che avvolge tutto il contenuto.
 *
 * Le sezioni si passano con `[sections]` (key/label/icon) oppure, per casi
 * semplici, con `[labels]="['Details']"` (key = label).
 * Il contenuto va proiettato; la visibilita' la gestisce il consumer con `@if`.
 *
 * @example
 * <fq-dialog-nav [labels]="['Details']" active="Details" variant="pill" enclosed>
 *   ...contenuto...
 * </fq-dialog-nav>
 */
@Component({
  selector: 'fq-dialog-nav',
  standalone: true,
  imports: [FontAwesomeModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div [class]="wrapperClass">
      @switch (resolvedVariant) {
        @case ('rail') {
          <nav role="tablist" aria-orientation="vertical" class="flex w-52 shrink-0 flex-col gap-1">
            @for (section of resolvedSections(); track section.key; let i = $index) {
              <button type="button" role="tab" [attr.aria-selected]="isActive(section.key)"
                class="fq-nav-rail-item" [class.is-active]="isActive(section.key)"
                (click)="select(section)" (keydown)="onKeydown($event, i)">
                @if (section.icon) { <fa-icon [icon]="section.icon" /> }
                <span class="truncate">{{ section.label }}</span>
                @if (hasBadge(section)) {
                  <span class="fq-nav-badge">{{ section.badge }}</span>
                }
              </button>
            }
          </nav>
        }
        @case ('wizard') {
          <div class="mb-5 flex items-center justify-center gap-2 overflow-x-auto">
            @for (section of resolvedSections(); track section.key; let i = $index) {
              <span class="fq-nav-step" [class.is-active]="isActive(section.key)" [class.is-done]="isDone(i)">
                <span class="fq-nav-step-dot">
                  @if (isDone(i)) { <fa-icon [icon]="['fas', 'check']" /> } @else { {{ i + 1 }} }
                </span>
                <span class="whitespace-nowrap">{{ section.label }}</span>
              </span>
              @if (i < resolvedSections().length - 1) { <span class="fq-nav-step-sep"></span> }
            }
          </div>
        }
        @case ('card') {
          <nav role="tablist" aria-orientation="horizontal" class="flex gap-1 overflow-x-auto px-1">
            @for (section of resolvedSections(); track section.key; let i = $index) {
              <button type="button" role="tab" [attr.aria-selected]="isActive(section.key)"
                class="fq-nav-card" [class.is-active]="isActive(section.key)"
                (click)="select(section)" (keydown)="onKeydown($event, i)">
                @if (section.icon) { <fa-icon [icon]="section.icon" /> }
                <span class="whitespace-nowrap">{{ section.label }}</span>
                @if (hasBadge(section)) {
                  <span class="fq-nav-badge">{{ section.badge }}</span>
                }
              </button>
            }
          </nav>
        }
        @case ('pill') {
          <div [class]="enclosedActive ? 'fq-nav-enclosed-head' : 'mb-4 flex justify-center'">
            <nav role="tablist" aria-orientation="horizontal"
              [class]="enclosedActive ? 'flex flex-wrap justify-center gap-1' : 'inline-flex flex-wrap gap-1 rounded-full border border-fq-border-soft bg-fq-surface-muted p-1'">
              @for (section of resolvedSections(); track section.key; let i = $index) {
                <button type="button" role="tab" [attr.aria-selected]="isActive(section.key)"
                  class="fq-nav-pill" [class.is-active]="isActive(section.key)"
                  (click)="select(section)" (keydown)="onKeydown($event, i)">
                  @if (section.icon) { <fa-icon [icon]="section.icon" /> }
                  <span class="whitespace-nowrap">{{ section.label }}</span>
                  @if (hasBadge(section)) {
                    <span class="fq-nav-badge">{{ section.badge }}</span>
                  }
                </button>
              }
            </nav>
          </div>
        }
        @default {
          <div [class]="enclosedActive ? 'fq-nav-enclosed-head' : 'mb-4 border-b border-fq-border-soft'">
            <nav role="tablist" aria-orientation="horizontal" class="flex gap-1">
              @for (section of resolvedSections(); track section.key; let i = $index) {
                <button type="button" role="tab" [attr.aria-selected]="isActive(section.key)"
                  class="fq-nav-tab" [class.is-active]="isActive(section.key)"
                  (click)="select(section)" (keydown)="onKeydown($event, i)">
                  @if (section.icon) { <fa-icon [icon]="section.icon" /> }
                  <span>{{ section.label }}</span>
                  @if (hasBadge(section)) {
                    <span class="fq-nav-badge">{{ section.badge }}</span>
                  }
                </button>
              }
            </nav>
          </div>
        }
      }
      <div [class]="contentClass"><ng-content></ng-content></div>
    </div>
  `
})
export class FqDialogNavComponent {
  readonly sections = input<FqDialogNavSection[]>([])
  /** Scorciatoia: sezioni definite solo da label (key = label). */
  readonly labels = input<string[]>()
  readonly active = model<string>('')
  readonly variant = input<FqDialogNavVariant>('auto')
  /** Avvolge barra + contenuto in un unico contenitore arrotondato (pill/tabs). */
  readonly enclosed = input(false, { transform: booleanAttribute })

  readonly resolvedSections = computed<FqDialogNavSection[]>(() => {
    const sections = this.sections()
    if (sections.length) {
      return sections
    }
    return (this.labels() ?? []).map(label => ({ key: label, label }))
  })

  get resolvedVariant (): Exclude<FqDialogNavVariant, 'auto'> {
    const variant = this.variant()
    if (variant !== 'auto') {
      return variant
    }
    const sections = this.resolvedSections()
    const hasLongLabels = sections.some(section => section.label.length > 14)
    return sections.length >= 5 || hasLongLabels ? 'rail' : 'tabs'
  }

  get isRail (): boolean {
    return this.resolvedVariant === 'rail'
  }

  get enclosedActive (): boolean {
    const variant = this.resolvedVariant
    return this.enclosed() && (variant === 'pill' || variant === 'tabs')
  }

  get wrapperClass (): string {
    if (this.isRail) {
      return 'flex gap-5'
    }
    return this.enclosedActive ? 'fq-nav-enclosed' : ''
  }

  get contentClass (): string {
    if (this.enclosedActive) {
      return 'fq-nav-enclosed-body'
    }
    return this.resolvedVariant === 'card' ? 'fq-nav-card-panel' : 'min-w-0 flex-1'
  }

  isActive (key: string): boolean {
    return this.active() === key
  }

  /** True se la sezione ha un contatore da mostrare accanto all'etichetta. */
  hasBadge (section: FqDialogNavSection): boolean {
    const badge = section.badge
    return badge !== null && badge !== undefined && badge !== ''
  }

  isDone (index: number): boolean {
    const activeIndex = this.resolvedSections().findIndex(section => section.key === this.active())
    return activeIndex > index
  }

  select (section: FqDialogNavSection): void {
    this.active.set(section.key)
  }

  onKeydown (event: KeyboardEvent, index: number): void {
    const sections = this.resolvedSections()
    if (!sections.length) {
      return
    }
    let next = index
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        next = (index + 1) % sections.length
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        next = (index - 1 + sections.length) % sections.length
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = sections.length - 1
        break
      default:
        return
    }
    event.preventDefault()
    this.select(sections[next])
    const buttons = (event.currentTarget as HTMLElement).parentElement?.querySelectorAll<HTMLButtonElement>('[role="tab"]')
    buttons?.[next]?.focus()
  }
}
